/**
 * product-3d-configurator.js — turns the PDP 3D viewer into a configurator.
 *
 * Takes over from product-3d.js on products that have a `branch_3d_configurator` metaobject entry
 * (see snippets/product-3d-configurator-data.liquid). It:
 *   1. lazy-loads assets/branch-3d-engine.js and assets/branch-3d-profiles.json on first open,
 *   2. composes the desk plus placed accessories in one three.js scene,
 *   3. projects each slot to a screen point every frame and hands it to the placement layer
 *      (assets/product-3d-placement.js), which owns the "+" pins and the slot picker,
 *   4. resolves each accessory's model through the endpoint the plain viewer already uses.
 *
 * Rule logic is a port of the reference configurator's rules.js, with one deliberate addition:
 * the original never checked slot KIND (a drawer validated into a side slot), so that gate is
 * added here explicitly.
 *
 * Nothing here knows about SKUs. Accessory variants are addressed by variant id, which the add-ons
 * payload already publishes and which the metaobject now stores directly.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- rules (ported from rules.js) */

  /** The GLBs are Y-up; the authored datum is Blender's (X, Y, Z). */
  function blenderToWeb(p) { return [p[0], p[2], -p[1]]; }

  function deskDimensions(profile, options) {
    var label = (options || {})[profile.sizeOption];
    var size = profile.sizes && profile.sizes[label];
    if (!size) return null;
    return { inches: size.inches, width: size.width_m, depth: size.depth_m, top: size.top_height_m, thickness: size.top_thickness_m,
      rear: size.rear_edge_m, recentreZ: size.recentre_z };
  }

  /** The desktop's rear edge (Blender Y). Duo tops are centred in depth; a profile whose top is not
   *  records the edge itself (Tria tops sit 4.6 mm behind the frame centre). */
  function rearEdge(dims) { return dims.rear != null ? dims.rear : dims.depth / 2; }
  function frontEdge(dims) { return rearEdge(dims) - dims.depth; }

  /* ---------------------------------------------------------------- merchandising size rules */

  /**
   * The desk's product.add_on_matches metafield (rendered as #bundle-variant-matches) decides which
   * accessory sizes each desk size sells. The 3D view applies the same two rules the add-on picker and
   * the cart's mismatch warning use:
   *   - options_with_no_matching: while the desk has option-name = option-value, the accessory's
   *     addon-option-name = addon-option-value does not fit (Duo 58″: no Large Desk Tray). Without an
   *     addon option the whole accessory is excluded for that desk value.
   *   - a variant-matches rule on the desk's size option with non-identical-values-to-match forces the
   *     accessory to the mapped value (Duo 36″: Small Desk Drawer).
   * Colour rules are preferences, not fit, and are ignored here. `known` is false when the page has no
   * rule for the accessory, so callers can fall back to the profile's reference data.
   */
  function sameValue(a, b) {
    return String(a == null ? '' : a).trim().toLowerCase() === String(b == null ? '' : b).trim().toLowerCase();
  }

  function optionValue(options, name) {
    var key = Object.keys(options || {}).find(function (k) { return sameValue(k, name); });
    return key === undefined ? undefined : options[key];
  }

  function sizeVerdict(rules, profile, handle, size, values) {
    var verdict = { known: false, blocked: false, forced: null };
    var entry = rules && Array.isArray(rules.matches) && rules.matches.find(function (e) { return e && e['add-on-handle'] === handle; });
    if (!entry) return verdict;
    verdict.known = true;
    var own = [size].concat(values || []);
    (entry.options_with_no_matching || []).forEach(function (rule) {
      if (!sameValue(optionValue(rules.options, rule['option-name']), rule['option-value'])) return;
      if (!rule['addon-option-value'] || own.some(function (value) { return sameValue(value, rule['addon-option-value']); })) verdict.blocked = true;
    });
    (entry['variant-matches'] || []).forEach(function (rule) {
      if (!profile || !profile.sizeOption || !sameValue(rule['option-name'], profile.sizeOption)) return;
      var current = optionValue(rules.options, rule['option-name']);
      (rule['non-identical-values-to-match'] || []).forEach(function (pair) {
        if (sameValue(pair.value, current)) verdict.forced = pair['add-on-value'];
      });
    });
    return verdict;
  }

  function addonMatches(productId) {
    var data = readJson('#main-product-item-' + productId + ' #bundle-variant-matches') || readJson('#bundle-variant-matches');
    return Array.isArray(data) ? data : null;
  }

  var ORIGIN_MOUNT = { attachment: 'origin' };
  function mountFor(profile, handle, size, slot) {
    // Headrests are authored at assembled world coordinates, so they attach at the chair's own
    // origin with no transform. Undoing the chair's recentring already puts it in that space.
    if (slot && (slot.kind === 'headrest' || slot.kind === 'tall-cylinder')) return ORIGIN_MOUNT;
    return profile.mounts[handle + '|' + (size || 'Standard')] || null;
  }

  /** A Large desk tray claims both inserts on its side. The only multi-slot rule in the system. */
  function occupiedSlots(item) {
    if (item.handle === 'desk-tray' && item.size === 'Large') {
      var side = item.slot.split('-')[0];
      return [side + '-front', side + '-rear'];
    }
    return [item.slot];
  }

  function occupant(items, slotId) {
    for (var i = 0; i < items.length; i++) {
      if (occupiedSlots(items[i]).indexOf(slotId) !== -1) return items[i];
    }
    return null;
  }

  /**
   * '' means allowed; any other string is shown to the shopper as the reason.
   * Checks in order: slot kind, size compatibility with the desk (the mount's geometry, then the
   * desk's add_on_matches rules when `rules` = { matches, options } is given), then occupancy.
   */
  function unavailable(profile, dims, slot, item, placedItems, ignoreSlot, rules) {
    var choices = profile.choices[slot.kind] || [];
    var kindOk = choices.some(function (c) { return c.handle === item.handle; });
    if (!kindOk) return 'That accessory does not fit this position.';

    var mount = mountFor(profile, item.handle, item.size, slot);
    if (!mount) return 'No mounting data for this accessory.';
    var notOnThisDesk = dims ? 'Not available on the ' + dims.inches + '″ desk.' : 'Not available on this desk.';
    if (dims && mount.compatibleInches && mount.compatibleInches.indexOf(dims.inches) === -1) return notOnThisDesk;
    var verdict = sizeVerdict(rules, profile, item.handle, item.size, item.options);
    if (verdict.blocked) return notOnThisDesk;
    var forced = item.size && item.size !== 'Standard' && verdict.forced;
    if (!forced && item.handle === 'desk-drawer' && dims) forced = (profile.drawerSizeByInches || {})[String(dims.inches)];
    if (forced && item.size !== forced) {
      return 'Choose the ' + forced.toLowerCase() + (item.handle === 'desk-drawer' ? ' drawer' : ' size') + ' for this desk.';
    }
    var others = placedItems.filter(function (i) { return i.slot !== ignoreSlot; });
    var wanted = occupiedSlots({ handle: item.handle, size: item.size, slot: slot.id });
    for (var i = 0; i < wanted.length; i++) {
      var taken = occupant(others, wanted[i]);
      if (taken) return 'Remove the ' + (taken.name || 'accessory').toLowerCase() + ' from this side first.';
    }
    return '';
  }

  /** `occupied` lists the filled slot ids. A mount with nestsAround shifts by its Blender-space nestOffset
   *  while that slot is filled (the Tria Cable Tray steps back around an In-Desk Power unit). */
  function placement(profile, dims, item, mount, occupied) {
    if (mount.attachment === 'origin') return { position: [0, 0, 0], rotationY: 0 };
    var underside = dims.top - dims.thickness;
    var point, rotationY = 0;
    if (mount.attachment === 'underside') {
      point = [0, -dims.depth / 2 + (mount.frontSetback != null ? mount.frontSetback : 0.2286), underside];
    } else if (mount.attachment === 'underside rear') {
      point = [0, rearEdge(dims) - (mount.rearSetback != null ? mount.rearSetback : 0.1048), underside];
      if (mount.nestsAround && mount.nestOffset && occupied && occupied.indexOf(mount.nestsAround) !== -1) {
        point = point.map(function (value, index) { return value + mount.nestOffset[index]; });
      }
    } else if (mount.attachment === 'top rear') {
      // Clamp-on accessories (Monitor Stand) are authored with their origin where the desktop's
      // top surface meets its rear edge.
      point = [0, rearEdge(dims), dims.top];
    } else { // side inserts
      var sign = item.slot.indexOf('left') === 0 ? -1 : 1;
      rotationY = sign * Math.PI / 2;
      // Inserts sit sideFrontSetback in from the front and rear edges; a Large tray spans both, so it
      // centres between them. The Duo is centred in depth (edges at ±depth/2); the Tria is not.
      var front = frontEdge(dims), rear = rearEdge(dims);
      var depthCoord = item.size === 'Large' ? (front + rear) / 2
        : (/-front$/.test(item.slot) ? front + profile.sideFrontSetback : rear - profile.sideFrontSetback);
      var vertical = mount.verticalOffset != null ? mount.verticalOffset : (item.handle === 'desk-hook' ? -0.001 : 0);
      point = [sign * (dims.width / 2 + (mount.mountEdgeY || 0)), depthCoord, underside + vertical];
    }
    return { position: blenderToWeb(point), rotationY: rotationY };
  }

  /** Empty desk pins sit on the wood where the accessory mounts. `occupied`: filled slot ids, as for placement(). */
  function pinPoint(profile, dims, slot, occupied) {
    if (slot.kind === 'tall-cylinder') return profile.pins && profile.pins['tall-cylinder'];
    if (slot.kind === 'headrest') {
      return (profile.pins && profile.pins.headrest) || [0, 1.04, -0.22];
    }
    if (!dims) return null;
    var w = dims.width, underside = dims.top - dims.thickness;
    if (slot.kind === 'side') {
      return blenderToWeb([(slot.id.indexOf('left') === 0 ? -1 : 1) * w / 2,
        /-front$/.test(slot.id) ? frontEdge(dims) + profile.sideFrontSetback : rearEdge(dims) - profile.sideFrontSetback, underside]);
    }
    var choices = profile.choices[slot.kind] || [];
    for (var i = 0; i < choices.length; i++) {
      var choice = choices[i];
      var size = choice.sizeFromDesk ? profile.drawerSizeByInches[String(dims.inches)] : choice.size || 'Standard';
      var mount = mountFor(profile, choice.handle, size, slot);
      if (!mount || (mount.compatibleInches && mount.compatibleInches.indexOf(dims.inches) === -1)) continue;
      if (mount.attachment === 'underside' || mount.attachment === 'underside rear' || mount.attachment === 'top rear') {
        var at = placement(profile, dims, { handle: choice.handle, size: size, slot: slot.id }, mount, occupied).position;
        // An optional Blender-space offset moves the pin to where the shopper sees the mount, e.g.
        // from under the desktop up to the cable cutout above an In-Desk Power unit.
        if (!mount.pinOffset) return at;
        var offset = blenderToWeb(mount.pinOffset);
        return at.map(function (value, index) { return value + offset[index]; });
      }
    }
    return null;
  }

  /* ---------------------------------------------------------------- page data */

  function readJson(selector, scope) {
    var el = (scope || document).querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /** Titles, prices and default variants, keyed by handle. */
  function addonSummary() {
    var rows = readJson('#full-pdp-addons-data') || [];
    var out = {};
    rows.forEach(function (r) { out[r.h] = { id: r.id, title: r.pt, price: r.p, defaultVariantId: r.dvid, image: r.di }; });
    return out;
  }

  /** Per-variant option values and images, keyed by handle. */
  function addonVariants(productId) {
    var byId = readJson('#add_on_product_data--' + productId) || {};
    var out = {};
    Object.keys(byId).forEach(function (k) {
      var p = byId[k];
      out[p.handle] = (p.variants || []).map(function (v) {
        return { id: v.id, options: v.options || [], image: v.featured_image && v.featured_image.src,
          available: v.available, configuratorEnabled: v.configuratorEnabled, price: v.price };
      });
    });
    return out;
  }

  /** Seed an accessory's finish from the desk's leg colour when that finish exists, else White.
   *  Mirrors defaultFinish() in the reference app.js. */
  function defaultFinish(variants, deskFinish) {
    if (deskFinish && variants.some(function (v) { return v.options.indexOf(deskFinish) !== -1; })) return deskFinish;
    if (variants.some(function (v) { return v.options.indexOf('White') !== -1; })) return 'White';
    return null;
  }

  /* ---------------------------------------------------------------- asset loading */

  var enginePromise = null;
  function loadEngine(src) {
    if (window.Branch3D) return Promise.resolve(window.Branch3D);
    if (enginePromise) return enginePromise;
    enginePromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { window.Branch3D ? resolve(window.Branch3D) : reject(new Error('engine loaded but Branch3D missing')); };
      s.onerror = function () { enginePromise = null; reject(new Error('engine failed to load')); };
      document.head.appendChild(s);
    });
    return enginePromise;
  }

  var profilesPromise = null;
  function loadProfiles(src) {
    if (!profilesPromise) {
      profilesPromise = fetch(src).then(function (r) {
        if (!r.ok) throw new Error('profiles ' + r.status);
        return r.json();
      }).catch(function (e) { profilesPromise = null; throw e; });
    }
    return profilesPromise;
  }

  /* ---------------------------------------------------------------- the controller */

  function renderingEnabled() {
    var bridge = window.BranchProduct3d;
    return !bridge || !bridge.isEnabled || bridge.isEnabled();
  }

  function Configurator(root) {
    this.root = root;
    this.config = readJson('[data-product-3d-configurator]', root);
    this.stage = root.querySelector('[data-product-3d-stage]');
    this.viewerEl = root.querySelector('[data-product-3d-viewer]');
    this.bindAutoRotateStop();
    this.engine = null;
    this.profile = null;
    this.dims = null;
    this.options = (this.config && this.config.options) || {};
    this.items = {};
    this.payloadCache = new Map();
    this.placement = null;
    this.hdState = 'idle';
    this.hdUrl = null;
    this.hdDeadline = null;
    this.upgraded = new Map();
    this.epoch = 0;
    this.active = false;
    this.busy = false;
    this.framed = false;
    this.bundleSelections = {};
    this.pendingAddon = null;
    this.cartItems = {};
    this.pendingCartItems = {};
    this.nativePlacementItems = {};
    this.nativeToggleItems = {};
    this.cartSlots = {};
    this.cartMatchPolicies = {};
    this.cartMatchContexts = {};
    this.managedHandles = {};
    this.cartSequence = 0;
    this.pendingPlacementCartId = null;
    this.resizeNotice = null;
    var cartInput = document.querySelector('#addon-products-for-atc');
    if (cartInput) {
      try { this.selectedAddons = JSON.parse(cartInput.value || '[]'); } catch (error) { this.selectedAddons = []; }
    }
    var self = this;
    document.addEventListener('branch:addon:selected', function (e) { self.addonSelected(e.detail); });
    document.addEventListener('branch:addon:toggle', function (e) { self.addonToggled(e.detail); });
    document.addEventListener('branch:addons:change', function (e) {
      if (!e.detail || String(e.detail.productId) !== String(self.config.productId)) return;
      self.selectedAddons = e.detail.items || [];
      self.selectionDirty = true;
      self.scheduleSelectionSync();
    });
  }

  Configurator.prototype.assetUrl = function (which) {
    return (this.config && this.config[which]) || null;
  };

  Configurator.prototype.isReady = function (payload) {
    return !!(renderingEnabled() && this.active && !this.busy && this.deskPayload && payload &&
      String(this.deskPayload.variantId) === String(payload.variantId) &&
      absolute(this.deskPayload.glb) === absolute(payload.glb));
  };

  Configurator.prototype.getPlacementCount = function () { return this.deskPayload && this.deskPayload.configuratorEnabled === false ? 0 : Object.keys(this.items).length; };

  Configurator.prototype.hidesHdWithAccessories = function () {
    var handles = {};
    (this.config.accessories || []).forEach(function (accessory) { if (accessory.handle) handles[accessory.handle] = true; });
    return this.getPlacementCount() > 0 && Object.keys(handles).length >= 3;
  };

  Configurator.prototype.hdSource = function (payload, configured) {
    // Keep an explicitly upgraded normal chair: its structured meshes can take the tall pose
    // without another download or a downgrade to the separately baked processed model.
    var normal = payload && absolute(payload.masterUrl);
    var normalUpgraded = normal && this.upgraded.has(String(payload.variantId) + ':' + normal);
    return this.hasTallCylinder(payload, configured || this.items) && this.hasTallModel(payload) && !normalUpgraded ? payload.tallModel : payload;
  };

  Configurator.prototype.canLoadHd = function () {
    var bridge = window.BranchProduct3d;
    return renderingEnabled() && (!bridge || !bridge.canLoadHd || bridge.canLoadHd());
  };

  Configurator.prototype.rememberedMaster = function (payload, configured) {
    if (!this.canLoadHd()) return null;
    var source = this.hdSource(payload, configured), url = source && absolute(source.masterUrl);
    return url && this.upgraded.has(String(payload.variantId) + ':' + url) ? url : null;
  };

  Configurator.prototype.refreshActions = function () {
    var bridge = window.BranchProduct3d;
    if (bridge && bridge.refreshActions) bridge.refreshActions(this.root);
    this.syncHdButton();
  };

  Configurator.prototype.begin = function () {
    this.cancelHdDeadline();
    this.busy = true;
    if (this.resetAccessoryGesture) this.resetAccessoryGesture();
    if (this.engine && this.engine.cancelPending) this.engine.cancelPending();
    if (this.placement) this.placement.hide();
    var bridge = window.BranchProduct3d;
    if (bridge && bridge.setStatus && !this.resizeNotice) bridge.setStatus(this.root, '');
    if (bridge && bridge.setLoading) bridge.setLoading(this.root, true);
    this.refreshActions();
    return ++this.epoch;
  };

  Configurator.prototype.current = function (epoch) { return renderingEnabled() && this.active && this.epoch === epoch; };

  Configurator.prototype.finish = function (epoch, error) {
    if (!this.current(epoch)) return false;
    this.busy = false;
    var bridge = window.BranchProduct3d;
    if (bridge && bridge.setLoading) bridge.setLoading(this.root, false);
    if (error && bridge && bridge.setStatus) bridge.setStatus(this.root, error);
    var selected = this.root.getAttribute('data-variant-id');
    if (this.placement && this.deskPayload && this.deskPayload.configuratorEnabled !== false && String(this.deskPayload.variantId) === String(selected)) {
      this.placement.setPlaced(this.placedMap()).setCatalog(this.buildCatalog()).show();
      this.projectPins();
    }
    this.refreshActions();
    if (this.pendingAddon && !(error && this.nativePlacementItems[this.pendingAddon.cartId])) {
      var pending = this.pendingAddon; this.pendingAddon = null; this.addonSelected(pending);
    } else if (this.active && this.deskPayload && this.deskPayload.configuratorEnabled !== false) {
      var id = Object.keys(this.pendingCartItems)[0], row = id && this.cartItems[id];
      if (row) this.addonSelected({ productId: this.config.productId, handle: row.productHandle, variantId: row.variantId, cartId: id });
    }
    if (this.selectionDirty) this.scheduleSelectionSync();
    return true;
  };

  Configurator.prototype.close = function () {
    this.cancelHdDeadline();
    ++this.epoch;
    this.active = false;
    this.busy = false;
    this.hdState = this.hdUrl ? 'done' : 'idle';
    this.selectedSlot = null;
    this.pendingAddon = null;
    this.cancelPendingPlacement();
    this.clearResizeNotice();
    this.hideToast();
    if (this.resetAccessoryGesture) this.resetAccessoryGesture();
    if (this.placement) this.placement.hide();
    if (this.engine && this.engine.pause) this.engine.pause();
    else if (this.engine && this.engine.cancelPending) this.engine.cancelPending();
    this.refreshActions();
  };

  /** A policy/viewport disable releases rendering resources but keeps confirmed purchases. */
  Configurator.prototype.suspend = function () {
    this.close();
    if (this.engine && this.engine.dispose) this.engine.dispose();
    this.engine = null;
    this.framed = false;
  };

  Configurator.prototype.open = function (payload) {
    if (!renderingEnabled()) return Promise.resolve();
    var self = this;
    var bridge = window.BranchProduct3d;
    if (bridge && bridge.ensureFetchBypass) bridge.ensureFetchBypass();
    var opening = !this.active;
    this.active = true;
    this.adoptNativeSizeSelections(opening, payload.options || this.config.options);
    var epoch = this.begin();
    var options = payload.options || this.config.options || {};
    var engineUrl = this.assetUrl('engineUrl'), profilesUrl = this.assetUrl('profilesUrl');
    return Promise.resolve().then(function () {
      if (!engineUrl || !profilesUrl || !payload.glb) throw new Error('Missing configurator assets');
      return Promise.all([loadEngine(engineUrl), loadProfiles(profilesUrl)]);
    }).then(function (res) {
      if (!self.current(epoch)) return;
      self.profile = res[1][self.config.profile];
      if (!self.profile) throw new Error('Unknown configurator profile');
      var dims = deskDimensions(self.profile, options);
      if (self.profile.sizeOption && !dims) throw new Error('Unknown desk size');
      if (!self.engine) self.engine = self.createEngine(res[0]);
      if (self.engine.resume) self.engine.resume();
      self.mountPlacement();
      var resizePlan = { updates: [], notes: [], blocked: [], removals: [], previewRemoved: false };
      return (payload.configuratorEnabled === false ? Promise.resolve(self.items) : self.reconcileItems(dims, options, resizePlan)).then(function (items) {
        if (!self.current(epoch)) return;
        var master = self.rememberedMaster(payload, items);
        var hdTimeoutMessage = '';
        return self.syncScene(payload, items, dims, master, { deferHdTimeout: true }).then(function (result) {
          if (!self.current(epoch) || !result || !result.hdTimeout) return result;
          // A remembered original can be evicted between visits. Keep the loading cover
          // while resolving the selected variant's processed scene, rather than leaving
          // the previous variant visible after its original exceeds the deadline.
          master = null;
          hdTimeoutMessage = result.message;
          return self.syncScene(payload, items, dims, null, { forceProcessed: true });
        }).then(function (result) {
          if (!self.current(epoch) || !result || !result.ok) return;
          // A native checkbox can be cleared while GLB decoding is still in flight. Keep
          // the loading cover and reconcile again before presenting that obsolete selection.
          if (Object.keys(items).some(function (slotId) {
            var id = items[slotId].cartId;
            return id && !self.cartItems[id];
          })) return self.open(payload);
          self.options = options;
          self.dims = dims;
          var previous = self.items;
          self.items = items;
          self.deskPayload = payload;
          self.hdUrl = master;
          self.hdState = master ? 'done' : 'idle';
          if (!self.framed) { self.engine.frame(); self.framed = true; }
          self.present(payload);
          if (self.engine.startAutoRotate) self.engine.startAutoRotate();
          self.commitResizePlan(resizePlan, previous);
          var messages = hdTimeoutMessage ? [hdTimeoutMessage] : [];
          if (resizePlan.notes.length) messages.push('Some accessory sizes changed to fit your desk. ' + resizePlan.notes.join(' '));
          if (resizePlan.blocked.length) messages.push(resizePlan.blocked.join(' '));
          else if (resizePlan.previewRemoved) messages.push('Accessories that no longer fit were removed from your configuration and selected addons.');
          var message = messages.join(' ');
          self.finish(epoch, message);
          if (message) self.showResizeNotice(message);
          // Add-ons chosen before opening that have no 3D model; desk-option changes re-run open() and stay quiet.
          if (opening && self.unshownSelections().length) self.showToast(NOT_SHOWN_MESSAGE);
        });
      });
    }).catch(function (error) {
      if (self.current(epoch)) console.error('[configurator]', error);
      self.finish(epoch, 'The 3D viewer could not be loaded.');
    });
  };

  Configurator.prototype.clearResizeNotice = function () {
    if (this.resizeNotice && window.clearTimeout) window.clearTimeout(this.resizeNotice.timer);
    this.resizeNotice = null;
  };

  Configurator.prototype.showResizeNotice = function (message) {
    this.clearResizeNotice();
    var self = this, notice = { text: message, epoch: this.epoch };
    this.resizeNotice = notice;
    if (window.BranchProduct3d && window.BranchProduct3d.setStatus) window.BranchProduct3d.setStatus(this.root, message);
    if (!window.setTimeout) return;
    notice.timer = window.setTimeout(function () {
      if (self.resizeNotice !== notice) return;
      self.resizeNotice = null;
      var status = self.root.querySelector('[data-product-3d-status]');
      // Selection-sync followups can advance the epoch while the shopper is still reading.
      // Keep this notice for its full lifetime, but never clear a newer operation's error.
      if (self.active && self.epoch >= notice.epoch && status && status.textContent === notice.text &&
          window.BranchProduct3d && window.BranchProduct3d.setStatus) window.BranchProduct3d.setStatus(self.root, '');
    }, 10000);
  };

  var NOT_SHOWN_MESSAGE = 'Not all selected add-ons are shown in the model';
  var TOAST_MS = 4000;

  /** Bottom-right notice that fades in, stays TOAST_MS and fades out (only while the viewer is open). */
  Configurator.prototype.showToast = function (text) {
    var toast = this.root.querySelector('[data-product-3d-toast]');
    if (!toast || !this.active || !window.setTimeout) return;
    window.clearTimeout(toast.__p3dToastTimer);
    toast.textContent = text;
    toast.hidden = false;
    void toast.offsetWidth; // start the fade from the hidden state
    toast.setAttribute('data-visible', 'true');
    toast.__p3dToastTimer = window.setTimeout(function () {
      toast.removeAttribute('data-visible');
      toast.__p3dToastTimer = window.setTimeout(function () { toast.hidden = true; }, 250);
    }, TOAST_MS);
  };

  Configurator.prototype.hideToast = function () {
    var toast = this.root && this.root.querySelector('[data-product-3d-toast]');
    if (!toast) return;
    if (window.clearTimeout) window.clearTimeout(toast.__p3dToastTimer);
    toast.removeAttribute('data-visible');
    toast.hidden = true;
  };

  /** Checked PDP add-ons this configurator cannot show: products outside its 3D accessories (a bundle
   *  counts as shown when it was selected as one of them). */
  Configurator.prototype.unshownSelections = function () {
    var owner = this.root.closest('.main-product__item') || document, shown = {}, summaries = addonSummary();
    (this.config.accessories || []).forEach(function (a) { if (a.id != null) shown[String(a.id)] = true; });
    Object.keys(this.bundleSelections || {}).forEach(function (handle) { if (summaries[handle]) shown[String(summaries[handle].id)] = true; });
    var inputs = owner.querySelectorAll('.addons__container input[data-addon-id]:checked');
    return Array.prototype.filter.call(inputs, function (input) { return !shown[String(input.getAttribute('data-addon-id'))]; });
  };

  /** A shopper picked an add-on with no 3D model while the viewer is open. Before it is ready, the
   *  check at the end of open() covers it. */
  Configurator.prototype.noteUnshownAddon = function () {
    if (this.active && this.isReady(this.deskPayload)) this.showToast(NOT_SHOWN_MESSAGE);
  };

  Configurator.prototype.present = function (payload) {
    var shell = this.viewerEl;
    if (!shell) return;
    shell.setAttribute('data-background', payload && payload.darkBackground === true ? 'dark' : 'light');
    shell.setAttribute('data-presented', 'true');
    var placeholder = this.root.querySelector('[data-product-3d-placeholder]');
    if (placeholder) placeholder.replaceChildren();
  };

  /** Master loading is explicit. A successful choice belongs to this exact variant and URL. */
  Configurator.prototype.syncHdButton = function () {
    var hd = this.root.querySelector('[data-product-3d-hd]');
    if (!hd) return;
    var payload = this.deskPayload, source = payload && this.hdSource(payload);
    if (!this.canLoadHd() || !this.isReady(payload) || String(payload.variantId) !== this.root.getAttribute('data-variant-id') ||
        !source.masterUrl || this.hidesHdWithAccessories() || this.hdState === 'done') {
      hd.hidden = true;
      hd.disabled = true;
      return;
    }
    var fmt = (window.BranchProduct3d && window.BranchProduct3d.formatMb) || function (b) { return Math.round(b / 1e6) + 'MB'; };
    hd.textContent = 'Load in 4K' + (source.masterBytes ? ' (' + fmt(Number(source.masterBytes)) + ')' : '');
    hd.disabled = false;
    hd.hidden = false;
  };

  Configurator.prototype.loadHd = function () {
    var self = this, payload = this.deskPayload, source = payload && this.hdSource(payload);
    if (!this.canLoadHd() || !this.isReady(payload) || !source.masterUrl || this.hdState === 'done' || this.hidesHdWithAccessories()) return Promise.resolve();
    var epoch = this.begin(), master = absolute(source.masterUrl);
    this.hdState = 'loading';
    return this.syncScene(payload, this.items, this.dims, master).then(function (result) {
      if (!self.current(epoch) || !result.ok) return;
      self.upgraded.set(String(payload.variantId) + ':' + master, true);
      self.hdUrl = master;
      self.hdState = 'done';
      self.finish(epoch);
    }).catch(function (error) {
      if (!self.current(epoch)) return;
      self.hdState = self.hdUrl ? 'done' : 'idle';
      console.error('[configurator] 4K swap failed', error);
      self.finish(epoch, 'The 4K model could not be loaded.');
    });
  };

  Configurator.prototype.createEngine = function (Branch3D) {
    var self = this;
    var engine = Branch3D.create(this.stage, {
      label: (this.config.productHandle || 'Product') + ' in 3D',
      // Hand three an ArrayBuffer rather than a URL: the Visually bypass patches window.fetch,
      // and going through it here keeps every model request on the native path.
      fetchBuffer: function (url, options) {
        return fetch(url, { credentials: 'omit', signal: options && options.signal }).then(function (r) {
          if (!r.ok) throw new Error('Model request failed (' + r.status + ')');
          return r.arrayBuffer();
        });
      },
    });
    engine.onFrame = function () { self.projectPins(); };
    this.bindAccessoryPicking(engine);
    return engine;
  };

  /**
   * The engine ends its opening spin on any drag, pinch, wheel or arrow key on the model. A press
   * or key on the pins and placement menus ends it too, so a "+" never moves under a finger. The
   * viewer's own buttons (close, fullscreen, AR, 4K) leave it spinning. The listener sits on the
   * viewer shell, which carries the canvas and pins into the fullscreen dialog as well.
   */
  Configurator.prototype.bindAutoRotateStop = function () {
    var self = this, shell = this.viewerEl;
    if (!shell) return;
    var chrome = '[data-product-3d-close], [data-product-3d-expand], [data-product-3d-ar], [data-product-3d-hd]';
    function stop(event) {
      if (event.target && event.target.closest && event.target.closest(chrome)) return;
      if (event.type === 'keydown' && event.key === 'Tab') return;
      if (self.engine && self.engine.stopAutoRotate) self.engine.stopAutoRotate();
    }
    shell.addEventListener('pointerdown', stop, true);
    // Screen readers and keyboards can activate a pin with a click and no pointerdown.
    shell.addEventListener('click', stop, true);
    shell.addEventListener('keydown', stop, true);
  };

  /** A stationary primary tap opens an accessory; orbit, pan and pinch remain camera gestures. */
  Configurator.prototype.bindAccessoryPicking = function (engine) {
    var self = this, canvas = engine.renderer && engine.renderer.domElement;
    if (!canvas || !engine.pickAccessory) return;
    var pointers = new Map(), gesture = null, tap = null;
    var hoverPoint = null, hoverTimer = null, idleCursor = canvas.style.cursor || '';
    function clearHover() {
      if (hoverTimer !== null && window.clearTimeout) window.clearTimeout(hoverTimer);
      hoverTimer = null; hoverPoint = null;
      canvas.style.cursor = idleCursor;
    }
    function resetGesture() { pointers.clear(); gesture = null; tap = null; clearHover(); }
    function visibilityChanged() { if (document.visibilityState === 'hidden') resetGesture(); }
    this.resetAccessoryGesture = resetGesture;
    if (window.addEventListener) window.addEventListener('blur', resetGesture);
    document.addEventListener('visibilitychange', visibilityChanged);
    engine.onDispose = function () {
      resetGesture();
      if (window.removeEventListener) window.removeEventListener('blur', resetGesture);
      if (document.removeEventListener) document.removeEventListener('visibilitychange', visibilityChanged);
      if (self.resetAccessoryGesture === resetGesture) self.resetAccessoryGesture = null;
    };
    function canPick() {
      return self.isReady(self.deskPayload) && self.deskPayload.configuratorEnabled !== false &&
        self.getPlacementCount() > 0 &&
        !engine.paused && !engine.disposed && self.placement && self.placement.mode !== 'placing' &&
        !(self.placement.isActive && !self.placement.isActive()) &&
        !document.querySelector('.tingle-modal--visible, [data-product-3d-qr]:not([hidden])');
    }
    function pickedSlot(x, y) {
      var slotId = engine.pickAccessory(x, y);
      return slotId && self.items[slotId] ? slotId : null;
    }
    function updateHover() {
      hoverTimer = null;
      if (!hoverPoint || pointers.size || !canPick()) { clearHover(); return; }
      canvas.style.cursor = pickedSlot(hoverPoint.x, hoverPoint.y) ? 'pointer' : idleCursor;
    }
    function trackHover(event) {
      if (event.pointerType === 'touch' || event.buttons || pointers.size || !canPick()) { clearHover(); return; }
      if (hoverPoint && hoverPoint.x === event.clientX && hoverPoint.y === event.clientY) return;
      hoverPoint = { x: event.clientX, y: event.clientY };
      // Coalesce fast pointer movement into at most 20 scene raycasts per second.
      if (hoverTimer === null) {
        if (window.setTimeout) hoverTimer = window.setTimeout(updateHover, 50);
        else updateHover();
      }
    }
    canvas.addEventListener('pointerdown', function (event) {
      clearHover();
      tap = null;
      pointers.set(event.pointerId, true);
      if (pointers.size !== 1 || event.isPrimary === false || event.button !== 0 || !canPick()) { gesture = null; return; }
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, at: Date.now(), epoch: self.epoch };
    });
    canvas.addEventListener('pointermove', function (event) {
      if (gesture && gesture.id === event.pointerId &&
          Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 5) gesture = null;
      trackHover(event);
    });
    canvas.addEventListener('pointerup', function (event) {
      pointers.delete(event.pointerId);
      if (gesture && gesture.id === event.pointerId && !pointers.size && self.epoch === gesture.epoch &&
          Date.now() - gesture.at < 700 && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) <= 5 && canPick()) {
        tap = { x: event.clientX, y: event.clientY, at: Date.now(), epoch: self.epoch };
      }
      gesture = null;
    });
    canvas.addEventListener('pointercancel', function (event) { pointers.delete(event.pointerId); gesture = null; tap = null; clearHover(); });
    canvas.addEventListener('pointerleave', clearHover);
    canvas.addEventListener('wheel', clearHover, { passive: true });
    canvas.addEventListener('click', function (event) {
      var picked = tap; tap = null;
      if (!picked || picked.epoch !== self.epoch || Date.now() - picked.at > 500 || event.button !== 0 || !canPick()) return;
      var slotId = pickedSlot(picked.x, picked.y);
      if (!slotId) return;
      // Keep the document's outside-click handler from immediately closing this new picker.
      event.stopPropagation();
      self.placement.openSlot(slotId);
    });
  };

  Configurator.prototype.mountPlacement = function () {
    if (this.placement || !window.BranchPlacement) return;
    var self = this;
    this.placement = window.BranchPlacement.mount(this.viewerEl);
    this.placement.setSlots(this.profile.slots).hide();
    this.viewerEl.addEventListener('branch:3d-placement:choose', function (e) { self.chooseAddon(e.detail.slotId, e.detail.choice); });
    this.viewerEl.addEventListener('branch:3d-placement:place', function (e) {
      self.place(e.detail.slotId, e.detail.choice);
    });
    this.viewerEl.addEventListener('branch:3d-placement:remove', function (e) { self.remove(e.detail.slotId); });
    this.viewerEl.addEventListener('branch:3d-placement:reset', function () { self.reset(); });
    this.viewerEl.addEventListener('branch:3d-placement:cancel', function () { self.cancelPendingPlacement(); });
  };

  /** Open the normal PDP accessory popup. Its Add action remains the owner of cart selection. */
  Configurator.prototype.chooseAddon = function (slotId, choice) {
    var handle = typeof choice === 'string' ? choice : choice.handle;
    if (!this.isReady(this.deskPayload) || this.deskPayload.configuratorEnabled === false) return;
    var owner = this.root.closest('.main-product__item[data-quick-view="false"]') || this.root.closest('.main-product__item');
    var tile = owner && owner.querySelector('.addons__container .checkbox-custom-label.quick-view-link[data-addon-handle="' + CSS.escape(handle) + '"][data-url]');
    if (!tile) return;
    var self = this, intent = { slotId: slotId, handle: handle, variantId: choice.variantId };
    this.selectedSlot = intent;
    if (this.placement) this.placement.closeSlot();
    var bridge = window.BranchProduct3d;
    var prepared = bridge && bridge.prepareAddonPopup ? bridge.prepareAddonPopup(this.root) : Promise.resolve();
    Promise.resolve(prepared).then(function () {
      if (self.selectedSlot !== intent) return;
      if (!self.isReady(self.deskPayload)) { self.selectedSlot = null; return; }
      // The tile's click handler means "remove" when already checked. Open its same popup
      // directly so a second placement can add another instance of the selected product.
      if (window.quickView && window.jQuery) window.quickView(window.jQuery(tile), 'quick-view-link');
      else tile.click();
    }).catch(function () { if (self.selectedSlot === intent) self.selectedSlot = null; });
  };

  /** Checkbox-driven selectors ensure one selection. Popup Add remains an explicit new instance. */
  Configurator.prototype.addonToggled = function (detail) {
    if (!detail || String(detail.productId) !== String(this.config.productId)) return Promise.resolve();
    if (!(this.config.accessories || []).some(function (accessory) { return accessory.handle === detail.handle; })) {
      if (detail.checked) this.noteUnshownAddon();
      return Promise.resolve();
    }
    if (!detail.checked) {
      this.deselectCartAddon(detail.handle);
      this.selectionDirty = true;
      this.scheduleSelectionSync();
      return Promise.resolve();
    }
    if (!detail.variantId) return Promise.resolve();
    var self = this;
    if (Object.keys(this.cartItems).some(function (id) { return self.cartItems[id].productHandle === detail.handle; })) return Promise.resolve();
    var stale = Object.keys(this.items).some(function (slotId) {
      var item = self.items[slotId];
      return item.handle === detail.handle && !self.selectionFor(item);
    });
    // A rapid No→With can arrive before the previous model's removal finishes.
    var removing = stale && this.isReady(this.deskPayload) ? this.syncSelectedAddons() : Promise.resolve();
    return Promise.all([removing, this.addonSelected(Object.assign({}, detail, { placement: null, checkboxSelection: true }))]);
  };

  Configurator.prototype.addonSelected = function (detail) {
    if (!detail || String(detail.productId) !== String(this.config.productId)) return Promise.resolve();
    if (detail.constituentVariantIds && detail.constituentVariantIds.length) {
      this.bundleSelections[detail.handle] = { variantId: detail.variantId, ids: detail.constituentVariantIds.map(String), constituents: detail.constituents || [] };
    }
    var offered = (this.config.accessories || []).some(function (accessory) { return accessory.handle === detail.handle; });
    if (!offered) { this.noteUnshownAddon(); return Promise.resolve(); }
    // While configuring, reserve the native purchase payload until its model is placed.
    // Replaying a queued Add must reuse this same instance.
    if (!(detail.constituentVariantIds || []).length) {
      detail = Object.assign({}, detail);
      if (detail.cartId && this.cartItems[detail.cartId]) {
        // Parent matching may have advanced a queued selection to a new exact variant.
        if (String(detail.variantId) !== String(this.cartItems[detail.cartId].variantId)) detail.variantId = this.cartItems[detail.cartId].variantId;
      } else detail.cartId = this.reserveCartItem(detail);
      if (detail.checkboxSelection) this.nativeToggleItems[detail.cartId] = true;
      this.pendingPlacementCartId = detail.cartId;
      this.publishCart();
    }
    if (!this.active) { this.pendingAddon = detail; return Promise.resolve(); }
    if (!this.isReady(this.deskPayload)) { this.pendingAddon = detail; return Promise.resolve(); }
    if (this.deskPayload.configuratorEnabled === false) return Promise.resolve();
    var self = this, catalog = this.buildCatalog();
    var hasPlacement = Object.prototype.hasOwnProperty.call(detail, 'placement');
    var remembered = hasPlacement ? detail.placement : this.selectedSlot;
    if (!hasPlacement) this.selectedSlot = null;
    if (remembered && remembered.handle === detail.handle) {
      var exact = (catalog[remembered.slotId] || []).find(function (choice) {
        return choice.handle === detail.handle && String(choice.variantId) === String(detail.variantId) && !choice.unavailable;
      });
      if (exact) return this.place(remembered.slotId, exact);
    }
    var eligible = this.profile.slots.map(function (slot) {
      // An Add action must not quietly replace another accessory on an occupied position.
      if (self.placedMap()[slot.id]) return null;
      var choice = (catalog[slot.id] || []).find(function (choice) {
        return choice.handle === detail.handle && String(choice.variantId) === String(detail.variantId) && !choice.unavailable;
      });
      return choice && { slotId: slot.id, choice: choice };
    }).filter(Boolean);
    if (eligible.length === 1) return this.place(eligible[0].slotId, eligible[0].choice);
    if (eligible.length && this.placement) this.placement.beginPlacement(detail.handle, detail.variantId);
    else {
      this.cancelPendingPlacement();
      if (window.BranchProduct3d && window.BranchProduct3d.setStatus) window.BranchProduct3d.setStatus(self.root, 'No compatible position is available. This accessory was not added.');
    }
    return Promise.resolve();
  };

  /** Occupancy is per position, not per slot kind. Include every finish and cable power variant. */
  Configurator.prototype.buildCatalog = function (overrideDims, overrideOptions) {
    var dims = overrideDims === undefined ? this.dims : overrideDims, options = overrideOptions || this.options;
    var self = this, summary = addonSummary(), variantsByHandle = addonVariants(this.config.productId);
    var offered = {};
    (this.config.accessories || []).forEach(function (a) { offered[a.handle] = a; });
    var finishSeed = options[this.profile.colourOption], rules = this.matchRules(options);
    var catalog = {}, placedItems = this.placedList();
    this.profile.slots.forEach(function (slot) {
      var owner = self.placedMap()[slot.id];
      var ignoreSlot = owner ? owner.slot : null;
      var list = [];
      (self.profile.choices[slot.kind] || []).forEach(function (choice) {
        if (!offered[choice.handle]) return;
        var variants = variantsByHandle[choice.handle] || [];
        if (choice.sizeFromDesk && !dims) return;
        var size = choice.sizeFromDesk ? self.profile.drawerSizeByInches[String(dims.inches)] : (choice.size || 'Standard');
        var preferred = defaultFinish(variants, finishSeed);
        variants.filter(function (v) {
          return (size === 'Standard' || v.options.indexOf(size) !== -1) &&
            (!choice.variantOption || v.options.indexOf(choice.variantOption) !== -1);
        })
          .forEach(function (variant) {
            var info = summary[choice.handle] || {};
            var values = variant.options.filter(function (value) { return value !== size && value !== 'Default Title'; });
            var finish = values[0] || preferred;
            var reason = unavailable(self.profile, dims, slot, { handle: choice.handle, size: size, options: variant.options }, placedItems, ignoreSlot, rules);
            if (variant.available === false) reason = 'This combination is sold out.';
            if (variant.configuratorEnabled === false) reason = 'This combination is not available in 3D.';
            list.push({ handle: choice.handle, size: size, finish: finish, modifier: choice.modifier,
              productName: info.title || choice.handle,
              name: (info.title || choice.handle) + (variant.options.length ? ' — ' + variant.options.join(' / ') : ''),
              variantId: variant.id, options: variant.options, image: variant.image || info.image,
              price: variant.price || info.price, unavailable: reason || undefined,
              matching: self.selectionMatchesParent(choice.handle, variant.id, options),
              preferred: finish === preferred });
          });
      });
      catalog[slot.id] = list.sort(function (a, b) { return Number(b.preferred) - Number(a.preferred); });
    });
    return catalog;
  };

  Configurator.prototype.placedList = function (items) {
    var list = items || this.items;
    return Object.keys(list).map(function (slotId) {
      var it = list[slotId];
      return { slot: slotId, handle: it.handle, size: it.size, name: it.name };
    });
  };

  /** Instances are independent from the slider's single checkbox/radio per product.
   * The cart still consumes its existing input; only configured handles are replaced here. */
  Configurator.prototype.reserveCartItem = function (detail) {
    var self = this, selectionOptions = detail.parentOptions || this.options;
    // A checkbox selection made before opening 3D still belongs to the shopper. Adopt it
    // before adding the newly requested instance rather than replacing it with this one.
    if (!this.managedHandles[detail.handle]) {
      (detail.previousCartRows || []).filter(function (row) { return row.productHandle === detail.handle; }).forEach(function (row) {
        var priorId = '3d-' + (++self.cartSequence);
        self.cartItems[priorId] = Object.assign({}, row, { quantity: Math.max(1, Number(row.quantity) || 1) });
        delete self.cartItems[priorId].configuratorInstances;
        self.cartMatchContexts[priorId] = Object.assign({}, selectionOptions);
        self.cartMatchPolicies[priorId] = self.profile ? self.followPolicyFor(row.productHandle, row.variantId, selectionOptions) : null;
        if (self.active && (!self.deskPayload || self.deskPayload.configuratorEnabled !== false)) self.pendingCartItems[priorId] = true;
        else if (!self.active) self.nativePlacementItems[priorId] = true;
      });
    }
    var id = '3d-' + (++this.cartSequence);
    var row = detail.cartRow || (this.selectedAddons || []).find(function (item) {
      return item.productHandle === detail.handle && String(item.variantId) === String(detail.variantId);
    }) || { productHandle: detail.handle, variantId: String(detail.variantId), quantity: 1, shipping: '', bundle: false, bundleConstituents: [], mainProductQuantity: 0 };
    this.cartItems[id] = Object.assign({}, row, { productHandle: detail.handle, variantId: String(detail.variantId), quantity: Math.max(1, (Number(row.quantity) || 1) / ((row.configuratorInstances || []).length || 1)) });
    delete this.cartItems[id].configuratorInstances;
    this.cartMatchContexts[id] = Object.assign({}, selectionOptions);
    this.cartMatchPolicies[id] = this.profile ? this.followPolicyFor(detail.handle, detail.variantId, selectionOptions) : null;
    if (this.active && (!this.deskPayload || this.deskPayload.configuratorEnabled !== false)) this.pendingCartItems[id] = true;
    else if (!this.active) this.nativePlacementItems[id] = true;
    this.managedHandles[detail.handle] = true;
    return id;
  };

  Configurator.prototype.cartIdFor = function (choice) {
    var id = this.pendingPlacementCartId, row = id && this.cartItems[id];
    if (row && row.productHandle === choice.handle && String(row.variantId) === String(choice.variantId)) return id;
    return this.reserveCartItem({ handle: choice.handle, variantId: choice.variantId });
  };

  Configurator.prototype.mergeCartRows = function (rows) {
    var self = this, grouped = {}, result = (rows || []).filter(function (row) { return !self.managedHandles[row.productHandle]; });
    Object.keys(this.cartItems).forEach(function (id) {
      if (self.pendingCartItems[id]) return;
      var row = self.cartItems[id], key = row.productHandle + ':' + row.variantId;
      if (!grouped[key]) {
        grouped[key] = Object.assign({}, row, { quantity: 0, configuratorInstances: [] });
        result.push(grouped[key]);
      }
      grouped[key].quantity += row.quantity;
      grouped[key].configuratorInstances.push(id);
    });
    return result;
  };

  Configurator.prototype.discardCartItem = function (id) {
    delete this.cartItems[id];
    delete this.cartSlots[id];
    delete this.cartMatchPolicies[id];
    delete this.cartMatchContexts[id];
    delete this.pendingCartItems[id];
    delete this.nativePlacementItems[id];
    delete this.nativeToggleItems[id];
    if (this.pendingPlacementCartId === id) this.pendingPlacementCartId = null;
    if (this.pendingAddon && this.pendingAddon.cartId === id) this.pendingAddon = null;
  };

  Configurator.prototype.cancelPendingPlacement = function () {
    var self = this, changed = false;
    Object.keys(this.pendingCartItems).forEach(function (id) { self.discardCartItem(id); changed = true; });
    if (changed) this.publishCart();
  };

  /** Only a successful scene commit may remove a previously placed purchase instance. */
  Configurator.prototype.syncPlacedCart = function (previous) {
    var self = this, visible = {};
    Object.keys(this.items).forEach(function (slotId) {
      var id = self.items[slotId].cartId;
      if (!id) return;
      visible[id] = true;
      self.cartSlots[id] = slotId;
      delete self.pendingCartItems[id];
      delete self.nativePlacementItems[id];
      delete self.nativeToggleItems[id];
      if (self.pendingPlacementCartId === id) self.pendingPlacementCartId = null;
      if (self.pendingAddon && self.pendingAddon.cartId === id) self.pendingAddon = null;
    });
    Object.keys(this.cartSlots).forEach(function (id) { if (!visible[id]) self.discardCartItem(id); });
    Object.keys(previous || {}).forEach(function (slotId) {
      var item = previous[slotId];
      if (item.cartId && !visible[item.cartId]) self.discardCartItem(item.cartId);
      if (!item.cartId && !Object.keys(self.items).some(function (id) { return self.items[id].handle === item.handle; })) self.unselectAddon(item.handle);
    });
    this.publishCart();
  };

  Configurator.prototype.deselectCartAddon = function (handle) {
    var self = this;
    Object.keys(this.cartItems).forEach(function (id) {
      if (self.cartItems[id].productHandle === handle) self.discardCartItem(id);
    });
    this.pendingPlacementCartId = null;
    if (this.pendingAddon && this.pendingAddon.handle === handle) this.pendingAddon = null;
    if (this.selectedSlot && this.selectedSlot.handle === handle) this.selectedSlot = null;
    delete this.managedHandles[handle];
  };

  Configurator.prototype.publishCart = function () {
    var input = document.querySelector('#addon-products-for-atc');
    if (!input) return;
    var rows;
    try { rows = JSON.parse(input.value || '[]'); } catch (error) { rows = []; }
    rows = this.mergeCartRows(rows);
    input.value = JSON.stringify(rows);
    input.setAttribute('value', input.value);
    var owner = this.root.closest('.main-product__item') || document, summary = addonSummary(), self = this;
    Object.keys(this.managedHandles).forEach(function (handle) {
      var info = summary[handle];
      if (!info) return;
      var count = rows.filter(function (row) { return row.productHandle === handle; }).reduce(function (sum, row) { return sum + Number(row.quantity); }, 0);
      var pendingToggle = Object.keys(self.nativeToggleItems).some(function (id) {
        return self.pendingCartItems[id] && self.cartItems[id] && self.cartItems[id].productHandle === handle;
      });
      owner.querySelectorAll('.addons-variants .item[data-addon-handle="' + CSS.escape(handle) + '"]').forEach(function (variants) {
        variants.classList.toggle('disabled', count === 0);
        variants.style.display = count ? '' : 'none';
      });
      owner.querySelectorAll('.addons__container input[data-addon-id="' + CSS.escape(String(info.id)) + '"]').forEach(function (checkbox) {
        var checked = count > 0 || pendingToggle;
        if (checkbox.checked !== checked) {
          checkbox.checked = checked;
          // Notify external selectors through the real checkbox without re-entering the
          // native cart writer for an already committed configurator state.
          var event = new Event('change', { bubbles: true });
          event.branchConfiguratorSync = true;
          checkbox.dispatchEvent(event);
        }
        var tile = checkbox.closest('.item');
        if (tile) { tile.setAttribute('data-active', String(count > 0)); tile.setAttribute('data-configurator-quantity', String(count)); }
      });
    });
    var count = rows.reduce(function (sum, row) { return sum + (Number(row.quantity) || 1); }, 0);
    owner.querySelectorAll('.amount-selected').forEach(function (badge) { badge.textContent = String(count); });
    document.dispatchEvent(new CustomEvent('branch:configurator:cart', { detail: { productId: String(this.config.productId) } }));
    document.dispatchEvent(new CustomEvent('branch:addons:change', { detail: { productId: String(this.config.productId), items: rows } }));
    // Retain ownership after removal: a delayed native radio rebuild must not resurrect it.
  };

  /** Read the selection prepared by the existing popup, including composite bundle variants. */
  Configurator.prototype.selectionFor = function (item) {
    if (item.cartId && this.pendingCartItems[item.cartId]) return this.cartItems[item.cartId] || null;
    if (!this.selectedAddons) return { variantId: item.variantId };
    var direct = this.selectedAddons.find(function (row) {
      if (item.cartId) return (row.configuratorInstances || []).indexOf(item.cartId) !== -1;
      return row.productHandle === item.handle;
    });
    if (direct) return direct;
    var bundle = this.bundleSelections[item.handle], ids = [];
    this.selectedAddons.forEach(function (row) {
      ids.push(String(row.variantId));
      (row.bundleConstituents || []).forEach(function (part) { ids.push(String(part.variantId || part.bundleConstituentId)); });
    });
    if (bundle && bundle.ids.length && bundle.ids.every(function (id) { return ids.indexOf(id) !== -1; })) return { variantId: bundle.variantId };
    return null;
  };

  // A desk-size change only changes a purchasable accessory's size when the desk's add_on_matches
  // rules say so: a forced value, or another offered size when the current one stops fitting.
  // Pages without rules keep the Duo's reference rules. Finish, power configuration, quantity and
  // placement identity are preserved.
  function resizedAccessorySize(profileName, profile, dims, handle, size, rules) {
    if (!dims) return size;
    var verdict = sizeVerdict(rules, profile, handle, size);
    if (verdict.known) {
      if (verdict.forced && size && size !== 'Standard') return verdict.forced;
      if (!verdict.blocked) return size;
      var sizes = [];
      Object.keys(profile.choices || {}).forEach(function (kind) {
        profile.choices[kind].forEach(function (choice) {
          if (choice.handle === handle && choice.size && sizes.indexOf(choice.size) === -1) sizes.push(choice.size);
        });
      });
      return sizes.find(function (other) { return other !== size && !sizeVerdict(rules, profile, handle, other).blocked; }) || size;
    }
    if (profileName !== 'duo') return size;
    if (handle === 'desk-drawer') return profile.drawerSizeByInches[String(dims.inches)] || size;
    if (handle === 'desk-tray' && dims.inches === 58) return 'Small';
    if (handle === 'cable-organizer' && dims.inches === 36) return 'Small';
    return size;
  }

  // The native cart expands bundle rows into their constituent variants. A resized
  // bundle is safe when its new row includes that complete purchasable payload.
  function resizableCartRow(row) {
    return row && (!row.bundle || (Array.isArray(row.bundleConstituents) && row.bundleConstituents.length > 0 &&
      row.bundleConstituents.every(function (part) {
        return part && Number.isSafeInteger(Number(part.variantId)) && Number(part.variantId) > 0 &&
          Number.isInteger(Number(part.quantity)) && Number(part.quantity) > 0;
      })));
  }

  Configurator.prototype.adoptNativeSizeSelections = function (automatic, options) {
    var self = this, changed = false, input = document.querySelector('#addon-products-for-atc');
    if (!input) return;
    var rows;
    try { rows = JSON.parse(input.value || '[]'); } catch (error) { return; }
    var owned = Object.assign({}, this.managedHandles);
    rows.forEach(function (row) {
      if (owned[row.productHandle] || !resizableCartRow(row) || !(self.config.accessories || []).some(function (accessory) { return accessory.handle === row.productHandle; })) return;
      // Quantity is the native purchase ratio for one configured instance. Only explicit
      // instance IDs represent duplicate placements; preserve both when rebuilding a viewer.
      var count = Math.max(1, (row.configuratorInstances || []).length);
      for (var index = 0; index < count; index++) {
        var id = '3d-' + (++self.cartSequence);
        self.cartItems[id] = Object.assign({}, row, { quantity: Math.max(1, (Number(row.quantity) || 1) / count) });
        delete self.cartItems[id].configuratorInstances;
        self.cartMatchContexts[id] = Object.assign({}, options || self.options);
        if (automatic) self.nativePlacementItems[id] = true;
      }
      self.managedHandles[row.productHandle] = true;
      changed = true;
    });
    // Composite popup events do not reserve a second cart row: the native bundle purchase
    // is already in this input. Link its queued intent to the adopted exact instance so
    // the successful first scene clears it instead of replaying Add into an occupied slot.
    var pending = this.pendingAddon;
    if (pending && !pending.cartId && (pending.constituentVariantIds || []).length) {
      var matching = Object.keys(this.cartItems).filter(function (id) {
        var row = self.cartItems[id];
        return row.productHandle === pending.handle && String(row.variantId) === String(pending.variantId);
      });
      if (matching.length === 1) {
        pending.cartId = matching[0];
        if (automatic) this.nativePlacementItems[pending.cartId] = true;
      }
    }
    if (changed) this.publishCart();
  };

  /** The desk's add_on_matches rules for size fit, evaluated against `options` (default: current). */
  Configurator.prototype.matchRules = function (options) {
    return { matches: addonMatches(this.config.productId), options: options || this.options };
  };

  Configurator.prototype.cartRowForVariant = function (variantId) {
    var factories = window.Branch && window.Branch.addons && window.Branch.addons.cartRowsForProduct;
    var factory = factories && factories[String(this.config.productId)];
    return factory ? factory()[String(variantId)] || null : null;
  };

  /** Follow-parent behavior is opt-in profile data. The existing PDP mappings resolve
   * option names and non-identical values; soft preferences never become requirements. */
  Configurator.prototype.matchCriteria = function (handle, options) {
    if (!this.profile || !Object.keys(this.profile.choices || {}).some(function (kind) {
      return this.profile.choices[kind].some(function (choice) { return choice.handle === handle && choice.followParent === true; });
    }, this)) return {};
    var mappings = readJson('#main-product-item-' + this.config.productId + ' #bundle-variant-matches') || readJson('#bundle-variant-matches') || [];
    if (!Array.isArray(mappings)) return {};
    var mapping = mappings.find(function (entry) { return entry['add-on-handle'] === handle; });
    var raw = readJson('#add_on_product_data--' + this.config.productId) || {};
    var product = Object.keys(raw).map(function (key) { return raw[key]; }).find(function (product) { return product.handle === handle; });
    var helper = window.Branch && window.Branch.product && window.Branch.product.returnMatchCriteriaObject;
    if (!mapping || !product || !Array.isArray(product.options) || !helper) return {};
    var rules = (mapping['variant-matches'] || []).filter(function (rule) { return rule['non-compulsory-match'] !== true && rule['non-compulsory-match'] !== 'true'; });
    var criteria = helper(rules, options || {}, product), result = {};
    Object.keys(criteria).forEach(function (key) {
      var values = criteria[key].filter(function (value) { return value !== undefined && value !== null; });
      if (values.length) result[key] = values;
    });
    return result;
  };

  Configurator.prototype.followPolicyFor = function (handle, variantId, options) {
    var criteria = this.matchCriteria(handle, options), policy = {};
    var variant = (addonVariants(this.config.productId)[handle] || []).find(function (variant) { return String(variant.id) === String(variantId); });
    if (!variant) return policy;
    Object.keys(criteria).forEach(function (index) { policy[index] = criteria[index].indexOf(variant.options[Number(index)]) !== -1; });
    return policy;
  };

  Configurator.prototype.selectionMatchesParent = function (handle, variantId, options) {
    var policy = this.followPolicyFor(handle, variantId, options), keys = Object.keys(policy);
    return keys.length > 0 && keys.every(function (key) { return policy[key]; });
  };

  /** Compute a combined size/matching substitution before looking up a purchasable variant.
   * Non-following options stay exact, including deliberately different finishes. */
  Configurator.prototype.updatedAccessoryChoice = function (item, dims, parentOptions, variants) {
    var size = resizedAccessorySize(this.config.profile, this.profile, dims, item.handle, item.size, this.matchRules(parentOptions));
    var sizeChanged = !!size && size !== item.size;
    var current = variants.find(function (variant) { return String(variant.id) === String(item.variantId); });
    var options = current ? current.options : item.options || [];
    var allowed = options.map(function (value) { return [value]; });
    var policy = item.cartId && this.cartMatchPolicies[item.cartId];
    if (!policy) {
      policy = this.followPolicyFor(item.handle, item.variantId, item.cartId && this.cartMatchContexts[item.cartId] || this.options);
      if (item.cartId) this.cartMatchPolicies[item.cartId] = policy;
    }
    var criteria = this.matchCriteria(item.handle, parentOptions), matchChanged = false;
    Object.keys(policy).forEach(function (index) {
      if (!policy[index] || !criteria[index]) return;
      allowed[Number(index)] = criteria[index];
      if (criteria[index].indexOf(options[Number(index)]) === -1) matchChanged = true;
    });
    if (!sizeChanged && !matchChanged) return null;
    if (sizeChanged) {
      var sizeIndex = options.indexOf(item.size);
      if (sizeIndex === -1) return { required: true, size: size, sizeChanged: true, matchChanged: matchChanged };
      allowed[sizeIndex] = [size];
    }
    var replacement = variants.find(function (variant) {
      return variant.available !== false && variant.configuratorEnabled !== false &&
        variant.options.length === allowed.length && variant.options.every(function (value, index) { return allowed[index].indexOf(value) !== -1; });
    });
    if (!replacement) return { required: true, size: size, sizeChanged: sizeChanged, matchChanged: matchChanged };
    return { required: true, size: size, sizeChanged: sizeChanged, matchChanged: matchChanged, choice: Object.assign({}, item, {
      size: size, variantId: replacement.id, options: replacement.options, image: replacement.image || item.image,
      finish: replacement.options.filter(function (value) { return value !== size && value !== 'Default Title'; })[0] || item.finish,
      price: replacement.price, name: (item.productName || item.handle) + ' — ' + replacement.options.join(' / ')
    }) };
  };

  /** Prepare scene and cart together; open() commits this plan only after every scene file loads. */
  Configurator.prototype.reconcileItems = function (dims, options, transaction) {
    var self = this, catalog = this.buildCatalog(dims, options), variants = addonVariants(this.config.productId);
    var rules = this.matchRules(options);
    var plan = transaction || { updates: [], notes: [], blocked: [], previewRemoved: false };
    plan.removals = [];
    plan.pending = [];
    var jobs = [], seen = {}, summaries = addonSummary();
    this.previewRemoved = false;
    Object.keys(this.items).forEach(function (slotId) {
      var item = self.items[slotId], selection = self.selectionFor(item);
      var key = item.cartId || item.handle + ':' + item.variantId;
      seen[key] = true;
      if (!selection) return;
      jobs.push({ item: item, slotId: slotId, selection: selection });
    });
    // Closed-view purchases get their default position as part of this same scene transaction.
    // Active-view purchases still wait for the shopper to choose a position.
    Object.keys(this.cartItems).forEach(function (cartId) {
      if (seen[cartId]) return;
      var row = self.cartItems[cartId], values = variants[row.productHandle] || [];
      var variant = values.find(function (value) { return String(value.id) === String(row.variantId); });
      if (!variant || variant.available === false || variant.configuratorEnabled === false) {
        blocked({ handle: row.productHandle, cartId: cartId });
        return;
      }
      var size = variant.options.find(function (value) { return value === 'Small' || value === 'Large'; });
      var summary = summaries[row.productHandle] || {};
      var choice;
      self.profile.slots.some(function (slot) {
        choice = (catalog[slot.id] || []).find(function (value) { return value.handle === row.productHandle && String(value.variantId) === String(row.variantId); });
        return !!choice;
      });
      jobs.push({ item: Object.assign({}, choice, { handle: row.productHandle, size: size || 'Standard', variantId: row.variantId,
        options: variant.options, cartId: cartId, productName: summary.title || row.productHandle }), slotId: self.cartSlots[cartId], selection: row });
    });
    function blocked(item, update) {
      var label = item.productName || (summaries[item.handle] || {}).title || item.handle;
      var text = label + (update && !update.sizeChanged ? ' could not be matched to this combination.' : ' could not be fitted to this desk.') + ' It was removed from your configuration and selected addons.';
      if (plan.blocked.indexOf(text) === -1) plan.blocked.push(text);
      if (item.cartId) plan.removals.push(item.cartId);
      plan.previewRemoved = true;
    }
    return Promise.all(jobs.map(function (job) {
      var original = job.item, item = original, selection = job.selection, slotId = job.slotId;
      if (String(selection.variantId) !== String(item.variantId)) {
        var selected = (catalog[slotId] || []).find(function (choice) {
          return choice.handle === item.handle && String(choice.variantId) === String(selection.variantId) && !choice.unavailable;
        });
        if (!selected) { blocked(item); return; }
        item = Object.assign({}, selected, { cartId: original.cartId });
      }
      var resize = self.updatedAccessoryChoice(item, dims, options, variants[item.handle] || []);
      if (!resize) {
        if (item === original && (item.url || item.modifier)) return { item: item, slotId: slotId };
        return self.modelForChoice(item).then(function (data) {
          if ((!data.glb && !data.modifier) || data.configuratorEnabled === false) { blocked(item); return; }
          return { item: Object.assign({}, item, { url: absolute(data.glb), masterBytes: data.masterBytes }), slotId: slotId };
        }).catch(function (error) {
          // A failed request must not discard a purchase made before opening the viewer.
          if (item.cartId && self.nativePlacementItems[item.cartId]) throw error;
          blocked(item);
        });
      }
      var choice = resize.choice;
      var cartRow = choice && self.cartRowForVariant(choice.variantId);
      if (!choice || !resizableCartRow(cartRow)) { blocked(item, resize); return; }
      var slot = self.profile.slots.find(function (candidate) {
        return (slotId ? candidate.id === slotId : true) && !unavailable(self.profile, dims, candidate, choice, [], null, rules);
      });
      if (!slot) { blocked(item, resize); return; }
      return self.modelForChoice(choice).then(function (data) {
        if ((!data.glb && !data.modifier) || data.configuratorEnabled === false) { blocked(item, resize); return; }
        var replacement = Object.assign({}, choice, { url: absolute(data.glb), masterBytes: data.masterBytes });
        var note = resize.sizeChanged ? (item.productName || (summaries[item.handle] || {}).title || item.handle) + ' changed to ' + resize.size.toLowerCase() + '.' : '';
        return { item: replacement, slotId: slotId, update: { cartId: item.cartId, handle: item.handle, fromVariantId: String(selection.variantId),
          variantId: String(choice.variantId), row: cartRow, slotId: slotId, note: note } };
      }).catch(function (error) {
        if (item.cartId && self.nativePlacementItems[item.cartId]) throw error;
        blocked(item, resize);
      });
    })).then(function (resolved) {
      var valid = {};
      // Resolve occupancy in stable instance order, never in model download completion order.
      resolved.forEach(function (result) {
        if (!result) return;
        var item = result.item, slotId = result.slotId;
        if (item.cartId && !self.cartItems[item.cartId]) return;
        var order = self.profile.defaultPlacementOrder || [];
        var slots = self.profile.slots.slice().sort(function (a, b) {
          var ai = order.indexOf(a.id), bi = order.indexOf(b.id);
          return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
        }).filter(function (slot) {
          return (!slotId || slotId === slot.id) && !unavailable(self.profile, dims, slot, item, self.placedList(valid), null, rules);
        });
        if (!slots.length) { blocked(item); return; }
        if (!slotId && slots.length > 1 && !self.nativePlacementItems[item.cartId]) {
          if (item.cartId) plan.pending.push(item.cartId);
        } else {
          slotId = slotId || slots[0].id;
          valid[slotId] = item;
        }
        if (result.update) { result.update.slotId = slotId; plan.updates.push(result.update); }
      });
      plan.notes = plan.updates.map(function (update) { return update.note; }).filter(function (note, index, notes) { return note && notes.indexOf(note) === index; });
      self.previewRemoved = plan.previewRemoved;
      return valid;
    });
  };

  Configurator.prototype.commitResizePlan = function (plan, previous) {
    var self = this, input = document.querySelector('#addon-products-for-atc'), rows = [];
    if (input) { try { rows = JSON.parse(input.value || '[]'); } catch (error) { rows = []; } }
    plan.updates.forEach(function (update) {
      if (update.cartId) {
        var current = self.cartItems[update.cartId];
        if (!current || String(current.variantId) !== update.fromVariantId) return;
        self.cartItems[update.cartId] = Object.assign({}, update.row, { variantId: update.variantId, quantity: current.quantity });
      } else {
        rows = rows.map(function (row) {
          return row.productHandle === update.handle && String(row.variantId) === update.fromVariantId
            ? Object.assign({}, update.row, { variantId: update.variantId, quantity: row.quantity }) : row;
        });
      }
      var owner = self.root.closest('.main-product__item') || document;
      owner.querySelectorAll('.addons-variants .item[data-addon-handle="' + CSS.escape(update.handle) + '"] input[type="radio"]').forEach(function (radio) {
        radio.checked = String(radio.value) === update.variantId;
      });
      var raw = readJson('#add_on_product_data--' + self.config.productId) || {};
      var product = Object.keys(raw).map(function (key) { return raw[key]; }).find(function (product) { return product.handle === update.handle; });
      var variant = product && (product.variants || []).find(function (variant) { return String(variant.id) === update.variantId; });
      var pricing = readJson('#full-pdp-addons-pricing-data') || {};
      if (variant && variant.featured_image && variant.featured_image.src && pricing[product.id] && pricing[product.id][update.variantId] &&
          window.Branch && window.Branch.product && window.Branch.product.updateAddonTile) {
        owner.querySelectorAll('.addons__container input[data-addon-id="' + CSS.escape(String(product.id)) + '"]').forEach(function (checkbox) {
          var tile = checkbox.closest('.item');
          if (!tile || !tile.querySelector('.addon-img img:not(.addons_lifestyle-image)') || !tile.querySelector('span.addon-price')) return;
          try { window.Branch.product.updateAddonTile(tile, variant); }
          catch (error) { console.error('[configurator] resized accessory thumbnail could not be updated', error); }
        });
      }
    });
    if (input) { input.value = JSON.stringify(rows); input.setAttribute('value', input.value); }
    (plan.removals || []).forEach(function (id) { self.discardCartItem(id); });
    (plan.pending || []).forEach(function (id) { self.pendingCartItems[id] = true; });
    if (previous) this.syncPlacedCart(previous);
    else this.publishCart();
  };

  /** Resolve one accessory variant's model through the endpoint the plain viewer already uses. */
  Configurator.prototype.modelFor = function (handle, variantId) {
    var key = handle + ':' + variantId;
    if (this.payloadCache.has(key)) return this.payloadCache.get(key);
    var section = this.root.getAttribute('data-availability-section') || 'product-3d-availability';
    var url = '/products/' + handle + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + section;
    var promise = fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('availability ' + r.status); return r.text(); })
      .then(function (html) {
        var m = html.match(/data-product-3d-availability[^>]*>([\s\S]*?)<\/script>/);
        if (!m) throw new Error('no availability payload for ' + handle);
        var data = JSON.parse(m[1].trim());
        if (String(data.variantId) !== String(variantId)) throw new Error('availability returned a different variant');
        return data;
      })
      .catch(function (e) { this.payloadCache.delete(key); throw e; }.bind(this));
    this.payloadCache.set(key, promise);
    return promise;
  };

  // The tall cylinder is a pose of the selected chair, not its own accessory GLB. Never fetch
  // the large structured original implicitly: the parent payload supplies a baked processed file.
  Configurator.prototype.modelForChoice = function (choice) {
    if (choice.modifier === 'tall-cylinder' && this.config.profile === 'erg-pro') {
      return Promise.resolve({ modifier: 'tall-cylinder' });
    }
    return this.modelFor(choice.handle, choice.variantId);
  };

  Configurator.prototype.hasTallCylinder = function (payload, configured) {
    var item = configured && configured['tall-cylinder'];
    return !!(payload && this.profile && this.config.profile === 'erg-pro' && payload.configuratorEnabled !== false &&
      item && item.modifier === 'tall-cylinder');
  };

  Configurator.prototype.hasTallModel = function (payload) {
    return !!(payload && payload.tallModel && payload.tallModel.glb && Number(payload.tallModel.masterBytes) > 0);
  };

  Configurator.prototype.tallCylinderLift = function (payload, configured, master) {
    if (!this.hasTallCylinder(payload, configured)) return 0;
    if (arguments.length < 3) master = this.hdUrl;
    var structuredMaster = this.canLoadHd() && master && master === absolute(payload.masterUrl);
    return this.hasTallModel(payload) || structuredMaster ? this.profile.tallCylinderLift || 0 : 0;
  };

  Configurator.prototype.place = function (slotId, choice) {
    var self = this;
    if (!this.isReady(this.deskPayload) || this.deskPayload.configuratorEnabled === false) return Promise.resolve();
    var slot = this.profile.slots.find(function (s) { return s.id === slotId; });
    if (!slot) return Promise.resolve();
    var existing = this.placedMap()[slotId], owner = existing ? existing.slot : slotId;
    var reason = unavailable(this.profile, this.dims, slot, choice, this.placedList(), owner, this.matchRules());
    var offered = (this.buildCatalog()[slotId] || []).some(function (c) {
      return !c.unavailable && c.handle === choice.handle && String(c.variantId) === String(choice.variantId) && c.size === choice.size;
    });
    if (reason || !offered) { this.cancelPendingPlacement(); return Promise.resolve(); }
    var pendingId = this.pendingPlacementCartId;
    var epoch = this.begin();
    return this.modelForChoice(choice).then(function (data) {
      if (!self.current(epoch)) return;
      if (pendingId && !self.cartItems[pendingId]) { self.finish(epoch); return; }
      if ((!data.glb && !data.modifier) || data.configuratorEnabled === false) throw new Error('Accessory model unavailable');
      var next = Object.assign({}, self.items);
      delete next[owner];
      next[slotId] = Object.assign({}, choice, { url: absolute(data.glb), masterBytes: data.masterBytes });
      return self.syncScene(self.deskPayload, next, self.dims, self.hdUrl).then(function (result) {
        if (!self.current(epoch) || !result.ok) return;
        if (pendingId && !self.cartItems[pendingId]) return self.open(self.deskPayload);
        var replaced = self.items[owner];
        var pendingRow = pendingId && self.cartItems[pendingId];
        var cartId = self.bundleSelections[choice.handle] ? null :
          (pendingRow && pendingRow.productHandle === choice.handle && String(pendingRow.variantId) === String(choice.variantId) ? pendingId : self.cartIdFor(choice));
        next[slotId].cartId = cartId;
        if (cartId) self.cartSlots[cartId] = slotId;
        var previous = self.items;
        self.items = next;
        if (replaced && replaced.cartId && replaced.cartId !== cartId) {
          self.discardCartItem(replaced.cartId);
        }
        if (self.pendingPlacementCartId === cartId) self.pendingPlacementCartId = null;
        self.syncPlacedCart(previous);
        self.finish(epoch);
      });
    }).catch(function (error) {
      if (!self.current(epoch)) return;
      if (pendingId && self.pendingCartItems[pendingId]) { self.discardCartItem(pendingId); self.publishCart(); }
      console.error('[configurator] place failed', error);
      self.finish(epoch, 'This accessory could not be loaded and was not added.');
    });
  };

  Configurator.prototype.replaceItems = function (next, resetCart) {
    var self = this;
    if (!this.isReady(this.deskPayload)) return Promise.resolve();
    var epoch = this.begin();
    return this.syncScene(this.deskPayload, next, this.dims, this.hdUrl).then(function (result) {
      if (!self.current(epoch) || !result.ok) return;
      var previous = self.items;
      self.items = next;
      if (resetCart) {
        Object.keys(self.cartItems).forEach(function (id) { self.discardCartItem(id); });
        self.pendingAddon = null;
        self.pendingPlacementCartId = null;
        self.selectedSlot = null;
      }
      self.syncPlacedCart(previous);
      self.finish(epoch);
      return true;
    }).catch(function () { self.finish(epoch, 'The configuration could not be updated.'); return false; });
  };

  /** Reuse the PDP's selection handlers so the existing main Add to Cart sees the same removal. */
  Configurator.prototype.unselectAddon = function (handle) {
    var info = addonSummary()[handle];
    var owner = this.root.closest('.main-product__item') || document;
    if (!info) return;
    var productIds = [String(info.id)], bundle = this.bundleSelections[handle];
    if (bundle) {
      var raw = readJson('#add_on_product_data--' + this.config.productId) || {};
      var rows = addonVariants(this.config.productId), summaries = addonSummary();
      Object.keys(raw).forEach(function (key) {
        if ((raw[key].variants || []).some(function (v) { return bundle.ids.indexOf(String(v.id)) !== -1; })) productIds.push(String(raw[key].id || key));
      });
      (bundle.constituents || []).forEach(function (part) {
        var info = summaries[part.productHandle];
        if (info) productIds.push(String(info.id));
      });
      Object.keys(rows).forEach(function (partHandle) {
        if (summaries[partHandle] && rows[partHandle].some(function (v) { return bundle.ids.indexOf(String(v.id)) !== -1; })) {
          productIds.push(String(summaries[partHandle].id));
        }
      });
    }
    productIds.filter(function (id, index) { return productIds.indexOf(id) === index; }).forEach(function (productId) {
      var inputs = owner.querySelectorAll('.addons__container input[data-addon-id="' + CSS.escape(productId) + '"]');
      for (var i = 0; i < inputs.length; i++) inputs[i].checked = false;
      if (inputs[0]) inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  Configurator.prototype.remove = function (slotId) {
    var placed = this.placedMap()[slotId];
    if (!placed) return Promise.resolve();
    var next = Object.assign({}, this.items);
    delete next[placed.slot];
    return this.replaceItems(next);
  };

  Configurator.prototype.reset = function () {
    return this.replaceItems({}, true);
  };

  Configurator.prototype.scheduleSelectionSync = function () {
    if (this.selectionScheduled) return;
    var self = this;
    this.selectionScheduled = true;
    Promise.resolve().then(function () {
      self.selectionScheduled = false;
      if (!self.selectionDirty || !self.isReady(self.deskPayload)) return;
      self.selectionDirty = false;
      self.syncSelectedAddons();
    });
  };

  /** Reflect removals and variant edits from the original popup, without adding cart requests. */
  Configurator.prototype.syncSelectedAddons = function () {
    var self = this, next = {}, changed = false;
    if (this.deskPayload.configuratorEnabled === false) return Promise.resolve();
    var catalog = this.buildCatalog();
    var work = Object.keys(this.items).map(function (slotId) {
      var item = self.items[slotId], selection = self.selectionFor(item);
      if (!selection) { changed = true; return; }
      if (String(item.variantId) === String(selection.variantId)) { next[slotId] = item; return; }
      changed = true;
      var choice = (catalog[slotId] || []).find(function (c) {
        return c.handle === item.handle && String(c.variantId) === String(selection.variantId) && !c.unavailable;
      });
      if (!choice) return;
      return self.modelForChoice(choice).then(function (data) {
        if ((data.glb || data.modifier) && data.configuratorEnabled !== false) next[slotId] = Object.assign({}, choice, {
          url: absolute(data.glb), masterBytes: data.masterBytes, cartId: item.cartId });
      });
    });
    if (!changed) return Promise.resolve();
    var epoch = this.begin();
    return Promise.all(work).then(function () {
      if (!self.current(epoch)) return;
      return self.syncScene(self.deskPayload, next, self.dims, self.hdUrl).then(function (result) {
        if (!self.current(epoch) || !result.ok) return;
        var previous = self.items;
        self.items = next;
        self.syncPlacedCart(previous);
        self.finish(epoch);
      });
    }).catch(function () { self.finish(epoch, 'The selected accessory could not be shown in 3D.'); });
  };

  Configurator.prototype.placedMap = function () {
    var out = {}, self = this;
    Object.keys(this.items).forEach(function (slotId) {
      var it = self.items[slotId];
      var choice = Object.assign({}, it, { slot: slotId });
      occupiedSlots({ slot: slotId, handle: it.handle, size: it.size }).forEach(function (claimed) { out[claimed] = choice; });
    });
    return out;
  };

  function absolute(url) { return url && url.indexOf('//') === 0 ? 'https:' + url : url; }

  Configurator.prototype.cancelHdDeadline = function () {
    if (this.hdDeadline) this.hdDeadline.cancel();
  };

  /** Bound original loading without timing an accessory-only update of a displayed master. */
  Configurator.prototype.syncWithHdDeadline = function (items, master, epoch) {
    this.cancelHdDeadline();
    var self = this, engine = this.engine;
    var displayed = engine.loaded && engine.loaded.get('desk');
    if (!master || (displayed && displayed.url === master) || !window.setTimeout) return engine.sync(items);
    var bridge = window.BranchProduct3d;
    var policy = bridge && bridge.hdLoadPolicy ? bridge.hdLoadPolicy(this.root) : {};
    var duration = Number(policy && policy.timeoutMs);
    if (!Number.isFinite(duration) || duration <= 0) duration = 5000;
    duration = Math.max(1000, Math.min(60000, duration));
    var message = policy && policy.message || '4K took too long to load. Your connection may be slow. Please try again.';
    var deadline = { expires: Date.now() + duration }, timer, settled = false;
    this.hdDeadline = deadline;
    return new Promise(function (resolve, reject) {
      function settle(result, error) {
        if (settled) return;
        settled = true;
        if (window.clearTimeout) window.clearTimeout(timer);
        if (self.hdDeadline === deadline) self.hdDeadline = null;
        if (error) reject(error); else resolve(result);
      }
      function expire() {
        if (settled) return;
        if (!self.current(epoch)) { settle({ ok: false, stale: true }); return; }
        if (engine.cancelPending) engine.cancelPending();
        settle({ ok: false, hdTimeout: true, message: message });
      }
      deadline.cancel = function () { settle({ ok: false, stale: true }); };
      timer = window.setTimeout(expire, duration);
      Promise.resolve().then(function () {
        if (settled || !self.current(epoch)) return { ok: false, stale: true };
        return engine.sync(items, { canCommit: function () {
          // Parsing can occupy the main thread past the timer's due time. The engine
          // checks this before changing any displayed mesh, pose or camera bounds.
          return !settled && self.current(epoch) && Date.now() < deadline.expires;
        }});
      }).then(function (result) {
        // A successful result has passed the engine's pre-commit deadline guard.
        // Do not label an already committed scene a timeout if final synchronous
        // bookkeeping crossed the boundary before this promise continuation ran.
        if (!settled && !(result && result.ok) && Date.now() >= deadline.expires) expire();
        else settle(result);
      }, function (error) {
        if (!settled && Date.now() >= deadline.expires) expire();
        else settle(null, error);
      });
    });
  };

  Configurator.prototype.syncScene = function (payload, configured, dims, master, options) {
    var epoch = this.epoch;
    var source = this.hdSource(payload, configured), desiredMaster = absolute(source.masterUrl);
    master = this.canLoadHd() && !(options && options.forceProcessed) ? (master && master === desiredMaster ? master : this.rememberedMaster(payload, configured)) : null;
    var lift = this.tallCylinderLift(payload, configured, master);
    var runtimeTall = !!(lift && master && master === absolute(payload.masterUrl));
    var base = lift && !runtimeTall && this.hasTallModel(payload) ? payload.tallModel : payload;
    var usingMaster = !!master, missingAuthoredCenter = false;
    this.tallPreviewUnavailable = !!(payload.configuratorEnabled !== false && configured['tall-cylinder'] && !lift);
    var regions = this.profile.selectionRegions || {};
    var selectionRegions = Object.keys(payload.configuratorEnabled === false ? {} : configured).filter(function (slotId) {
      return configured[slotId].modifier && regions[slotId];
    }).map(function (slotId) {
      var region = regions[slotId], maximum = region.max.slice();
      if (region.liftMaxY) maximum[1] += lift;
      return { id: slotId, min: region.min.slice(), max: maximum };
    });
    var items = [{ id: 'desk', url: usingMaster ? master : absolute(base.glb),
      masterBytes: usingMaster ? 0 : base.masterBytes,
      zOffset: dims && dims.recentreZ != null ? dims.recentreZ : (this.profile.recentreZ || 0),
      selectionRegions: selectionRegions, tallCylinder: runtimeTall }];
    var self = this;
    Object.keys(payload.configuratorEnabled === false ? {} : configured).forEach(function (slotId) {
      var it = configured[slotId], slot = self.profile.slots.find(function (x) { return x.id === slotId; });
      if (it.modifier === 'tall-cylinder') return;
      var mount = mountFor(self.profile, it.handle, it.size, slot);
      if (!mount || (mount.attachment !== 'origin' && !dims)) return;
      var t = placement(self.profile, dims, { handle: it.handle, size: it.size, slot: slotId }, mount, Object.keys(configured));
      if (slot && slot.kind === 'headrest' && lift) t.position[1] += lift;
      var authoredCenter;
      if (mount.authoredCenters) {
        var power = (it.options || []).find(function (value) { return Object.prototype.hasOwnProperty.call(mount.authoredCenters, value); });
        if (power) authoredCenter = mount.authoredCenters[power];
        else if (Number(it.masterBytes) > 15 * 1024 * 1024) missingAuthoredCenter = true;
      }
      items.push({ id: slotId, accessory: true, url: it.url, masterBytes: it.masterBytes, zOffset: 0,
        authoredCenter: authoredCenter, position: t.position, rotationY: t.rotationY });
    });
    if (missingAuthoredCenter) return Promise.reject(new Error('Mounting data is missing for this accessory power option.'));
    return this.syncWithHdDeadline(items, master, epoch).then(function (result) {
      if (result.hdTimeout) {
        if (self.current(epoch)) {
          self.upgraded.delete(String(payload.variantId) + ':' + master);
          self.hdState = self.hdUrl ? 'done' : 'idle';
          if (!(options && options.deferHdTimeout)) {
            self.finish(epoch, result.message);
            self.showResizeNotice(result.message);
          }
        }
        return result;
      }
      if (!result.ok && !result.stale) throw result.error || new Error('Scene could not be loaded');
      if (result.ok && self.current(epoch)) { self.hdUrl = master; self.hdState = usingMaster ? 'done' : 'idle'; }
      return result;
    });
  };

  Configurator.prototype.projectPins = function () {
    // The renderer can keep ticking after an initial load fails, before any payload commits.
    if (!renderingEnabled() || !this.active || this.busy || !this.placement || !this.engine || !this.profile ||
        !this.deskPayload || this.deskPayload.configuratorEnabled === false) return;
    var positions = {}, profile = this.profile, dims = this.dims, engine = this.engine;
    var lift = this.tallCylinderLift(this.deskPayload, this.items);
    var occupied = Object.keys(this.items || {});
    profile.slots.forEach(function (slot) {
      var point = pinPoint(profile, dims, slot, occupied);
      if (point && lift) {
        point = point.slice();
        if (slot.kind === 'headrest') point[1] += lift;
        else if (slot.kind === 'tall-cylinder') point[1] += lift / 2;
      }
      positions[slot.id] = point ? engine.project(point) : { visible: false };
    });
    this.placement.setPositions(positions);
  };

  /* ---------------------------------------------------------------- entry point */

  var registry = new WeakMap();
  window.BranchConfigurator = {
    open: function (root, payload) {
      if (!renderingEnabled()) return null;
      var instance = registry.get(root);
      if (!instance) { instance = new Configurator(root); registry.set(root, instance); }
      if (!instance.config || !instance.config.enabled) return null;
      instance.open(payload);
      return instance;
    },
    get: function (root) { return registry.get(root) || null; },
    // Pure placement rules, read by tools/shopify/product-3d-configurator-rules.test.mjs.
    rules: { deskDimensions: deskDimensions, mountFor: mountFor, placement: placement, pinPoint: pinPoint, unavailable: unavailable,
      sizeVerdict: sizeVerdict, resizedAccessorySize: resizedAccessorySize },
    takeAddonPlacement: function (productId, handle) {
      var root = document.querySelector('[data-product-3d][data-product-id="' + CSS.escape(String(productId)) + '"]');
      var instance = root && registry.get(root), intent = instance && instance.selectedSlot;
      if (!intent || intent.handle !== handle) return null;
      instance.selectedSlot = null;
      return intent;
    },
    addonPopupVariant: function (productId, handle) {
      var root = document.querySelector('[data-product-3d][data-product-id="' + CSS.escape(String(productId)) + '"]');
      var instance = root && registry.get(root), intent = instance && instance.selectedSlot;
      return intent && intent.handle === handle ? intent.variantId : null;
    },
    mergeCartRows: function (productId, rows) {
      var root = document.querySelector('[data-product-3d][data-product-id="' + CSS.escape(String(productId)) + '"]');
      var instance = root && registry.get(root);
      return instance ? instance.mergeCartRows(rows) : rows;
    },
    deselectCartAddon: function (productId, handle) {
      var root = document.querySelector('[data-product-3d][data-product-id="' + CSS.escape(String(productId)) + '"]');
      var instance = root && registry.get(root);
      if (instance) instance.deselectCartAddon(handle);
    },
  };
})();
